import {
  CRow,
  CCol,
  CCard,
  CCardBody,
  CButton,
  CFormInput,
  CFormSelect,
  CFormCheck,
  CInputGroup,
  CInputGroupText,
  CDropdown,
  CDropdownToggle,
  CDropdownMenu,
  CAlert,
} from '@coreui/react-pro'
import CIcon from '@coreui/icons-react'
import {
  cilCloudUpload,
  cilImage,
  cilUserPlus,
  cilSearch,
  cilColumns,
  cilPeople,
  cilCheckCircle,
  cilBan,
  cilWarning,
  cilSwapHorizontal,
  cilCalendar,
  cilTag,
  cilStar,
  cilOptions,
  cilTrash,
} from '@coreui/icons'
import React, { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Bootbox from 'bootbox-react'
import AddForm from './AddForm'
import Table from './Table'
import PlayerDetailPanel from './PlayerDetailPanel'
import InactivePlayer from './InactivePlayer'
import ChangeTeamModal from './BulkActions/ChangeTeamModal'
import SetAvailabilityModal from './BulkActions/SetAvailabilityModal'
import SetCategoryModal from './BulkActions/SetCategoryModal'
import PlayerService from 'src/service/PlayerService'
import CommonService from 'src/service/CommonService'
import ToastComponent from 'src/components/common/TaostComponent'
import useDebounce from 'src/hooks/useDebounce'
import StepperBar from '../SetupProgress/StepperBar'

const TABS = [
  { key: 'all', label: 'All Players', statKey: 'total' },
  { key: 'active', label: 'In-Use', statKey: 'active' },
  { key: 'inactive_player', label: 'Not In Use', statKey: 'inactive' },
  { key: 'inactive', label: 'Deactivated', statKey: 'inactive' },
  { key: 'unavailable', label: 'Unavailable', statKey: 'unavailable' },

  { key: 'needs_attention', label: 'Needs Attention', statKey: 'needs_attention' },
]

const OPTIONAL_COLUMNS = [
  { key: 'team_name', label: 'Team' },
  { key: 'position_name', label: 'Position' },
  { key: 'svalue', label: 'Value' },
  { key: 'availability', label: 'Availability' },
  { key: 'category_name', label: 'Category' },
  { key: 'sponsor_name', label: 'Sponsor' },
  { key: 'is_active', label: 'Status' },
]

const PCR_GATED_COLUMNS = ['category_name', 'is_active']

const Player = () => {
  const navigate = useNavigate()
  const [addPlayerVisible, setAddPlayerVisible] = useState(false)
  const [detailPlayerId, setDetailPlayerId] = useState(null)
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(false)
  const [activePage, setActivePage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(10)
  const [columnSorter, setColumnSorter] = useState({})
  const [gameType, setGameType] = useState({})
  const [stats, setStats] = useState({})
  const [teamList, setTeamList] = useState([])
  const [positionList, setPositionList] = useState([])
  const [pcrEnabled, setPcrEnabled] = useState(false)
  const [pcrTerm, setPcrTerm] = useState(null)
  const [categoryList, setCategoryList] = useState([])
  const [activeTab, setActiveTab] = useState('all')
  const [filters, setFilters] = useState({ search: '', team: '', position: '', category: '' })
  const [visibleColumns, setVisibleColumns] = useState(
    OPTIONAL_COLUMNS.reduce((acc, col) => ({ ...acc, [col.key]: true }), {}),
  )
  const [selectedItems, setSelectedItems] = useState([])
  const [changeTeamVisible, setChangeTeamVisible] = useState(false)
  const [setAvailabilityVisible, setSetAvailabilityVisible] = useState(false)
  const [setCategoryVisible, setSetCategoryVisible] = useState(false)
  const [bulkDeleteConfirmVisible, setBulkDeleteConfirmVisible] = useState(false)

  const dActivePage = useDebounce(activePage, 500)
  const dItemsPerPage = useDebounce(itemsPerPage, 500)
  const dFilters = useDebounce(filters, 500)
  const dColumnSorter = useDebounce(columnSorter, 500)

  useEffect(() => {
    PlayerService.getTeamPositionValueBatBowlStyle().then((result) => {
      setPositionList(result.position || [])
    })
    CommonService.gameStructureInfo().then((result) => {
      setPcrEnabled(Number(result?.game_data?.external_player_limit_status) === 1)
      const tagId = Number(result?.game_data?.external_player_tag)
      const tag = (result?.externalPlayerList || []).find((item) => Number(item.id) === tagId)
      setPcrTerm(tag || null)
    })
  }, [])

  useEffect(() => {
    if (!pcrEnabled && filters.category) {
      setFilters((prev) => ({ ...prev, category: '' }))
    }
  }, [pcrEnabled, filters.category])

  const fetchUsers = useCallback(async () => {
    if (activeTab === 'inactive_player') return
    setLoading(true)
    const offset = dItemsPerPage * dActivePage - dItemsPerPage
    let params = new URLSearchParams()
    if (dFilters.search) params.append('search', dFilters.search)
    if (dFilters.team) params.append('team_name', dFilters.team)
    if (dFilters.position) params.append('position_name', dFilters.position)
    if (dFilters.category) params.append('category', dFilters.category)
    params.append('tab', activeTab)
    dColumnSorter &&
      dColumnSorter.column !== undefined &&
      params.append('sort', `${dColumnSorter.column}%${dColumnSorter.state}`)

    try {
      const result = await PlayerService.getPlayers(offset, dItemsPerPage, dActivePage, params)
      setUsers(result.data)
      setGameType(result.game_type)
      setStats(result.stats || {})
      setTeamList(result.teamList || [])
      setCategoryList(result.exeternalPlayerList || [])
    } catch (error) {
      console.error('Error fetching users:', error)
    } finally {
      setLoading(false)
    }
  }, [dActivePage, dItemsPerPage, dFilters, activeTab, dColumnSorter])

  useEffect(() => {
    fetchUsers()
  }, [fetchUsers])

  const handleTabChange = (tabKey) => {
    setActivePage(1)
    setActiveTab(tabKey)
    setSelectedItems([])
  }

  const handleFilterChange = (key, value) => {
    setActivePage(1)
    setFilters((prev) => ({ ...prev, [key]: value }))
    setSelectedItems([])
  }

  useEffect(() => {
    setSelectedItems([])
  }, [activePage])

  const toggleColumn = (key) => {
    setVisibleColumns((prev) => ({ ...prev, [key]: !prev[key] }))
  }

  const optionalColumns = OPTIONAL_COLUMNS.filter(
    (col) => !PCR_GATED_COLUMNS.includes(col.key) || pcrEnabled,
  )
  const effectiveVisibleColumns = pcrEnabled
    ? visibleColumns
    : { ...visibleColumns, category_name: false, is_active: false }

  const selectedIds = selectedItems.map((item) => item.id)

  const handleBulkActionSuccess = () => {
    fetchUsers()
    setSelectedItems([])
  }

  const handleBulkDeleteConfirm = () => {
    setBulkDeleteConfirmVisible(false)
    PlayerService.deleteAllPlayer({ playerIds: selectedIds }).then((res) => {
      if (res.status === 200) {
        ToastComponent(res.message, 'success')
        handleBulkActionSuccess()
      } else {
        ToastComponent(res.message, 'error')
      }
    })
  }

  const handleComingSoon = () => {
    ToastComponent('Coming soon', 'info')
  }

  const STAT_CARDS = [
    { label: 'Total Players', value: stats.total ?? 0, icon: cilPeople, color: 'primary' },
    { label: 'Active', value: stats.active ?? 0, icon: cilCheckCircle, color: 'success' },
    { label: 'Unavailable Now', value: stats.unavailable ?? 0, icon: cilBan, color: 'danger' },
    {
      label: 'Missing Details',
      value: stats.needs_attention ?? 0,
      icon: cilWarning,
      color: 'info',
    },
  ]

  return (
    <>
      <StepperBar activeStepKey="players" />
      <div className="d-flex flex-wrap justify-content-between align-items-start gap-2 mb-3">
        <div>
          <h4 className="mb-1">Players</h4>
          <div className="text-medium-emphasis">
            Manage player records, availability, photos, sponsors and game status.
          </div>
        </div>
        <div className="d-flex flex-wrap gap-2">
          <CButton color="secondary" variant="outline" onClick={() => navigate('/player-import')}>
            <CIcon icon={cilCloudUpload} className="me-1" />
            Import Players
          </CButton>
          <CButton
            color="secondary"
            variant="outline"
            title="Player Profile"
            onClick={() => navigate('/players-profile')}
          >
            <CIcon icon={cilImage} /> Player Profile
          </CButton>
          <CButton color="primary" onClick={() => setAddPlayerVisible(true)}>
            <CIcon icon={cilUserPlus} className="me-1" />
            Add Player
          </CButton>
        </div>
      </div>

      {gameType == 4 && (
        <CAlert color="danger">
          Restriction: You can create a maximum of 30 Player Records only
        </CAlert>
      )}

      <CRow className="g-3 mb-3">
        {STAT_CARDS.map((s) => (
          <CCol sm={6} lg={3} key={s.label}>
            <CCard className="h-100">
              <CCardBody className="d-flex justify-content-between align-items-center">
                <div>
                  <div className="text-medium-emphasis small">{s.label}</div>
                  <div className="fs-4 fw-semibold">{s.value}</div>
                </div>
                <div
                  className={`d-flex align-items-center justify-content-center rounded-circle bg-${s.color}-subtle text-${s.color}`}
                  style={{ width: 42, height: 42 }}
                >
                  <CIcon icon={s.icon} size="lg" />
                </div>
              </CCardBody>
            </CCard>
          </CCol>
        ))}
      </CRow>

      <CCard>
        <CCardBody>
          <ul className="nav nav-underline mb-3">
            {TABS.map((tab) => (
              <li className="nav-item" key={tab.key}>
                <button
                  type="button"
                  className={`nav-link${activeTab === tab.key ? ' active fw-semibold' : ''}`}
                  onClick={() => handleTabChange(tab.key)}
                >
                  {tab.label}{' '}
                  <span className="badge bg-secondary-subtle text-secondary-emphasis ms-1">
                    {stats[tab.statKey] ?? 0}
                  </span>
                </button>
              </li>
            ))}
          </ul>

          {activeTab === 'inactive_player' ? (
            <InactivePlayer />
          ) : (
            <>
              <div className="d-flex flex-wrap gap-2 mb-3">
                <CInputGroup style={{ maxWidth: 280 }}>
                  <CInputGroupText>
                    <CIcon icon={cilSearch} />
                  </CInputGroupText>
                  <CFormInput
                    placeholder="Search by name or Player ID"
                    value={filters.search}
                    onChange={(e) => handleFilterChange('search', e.target.value)}
                  />
                </CInputGroup>
                <CFormSelect
                  style={{ maxWidth: 180 }}
                  value={filters.team}
                  onChange={(e) => handleFilterChange('team', e.target.value)}
                >
                  <option value="">All Teams</option>
                  {teamList.map((team) => (
                    <option value={team.name} key={team.id}>
                      {team.name}
                    </option>
                  ))}
                </CFormSelect>
                <CFormSelect
                  style={{ maxWidth: 180 }}
                  value={filters.position}
                  onChange={(e) => handleFilterChange('position', e.target.value)}
                >
                  <option value="">All Positions</option>
                  {positionList.map((position) => (
                    <option value={position.name} key={position.id}>
                      {position.name}
                    </option>
                  ))}
                </CFormSelect>
                {pcrEnabled && (
                  <CFormSelect
                    style={{ maxWidth: 180 }}
                    value={filters.category}
                    onChange={(e) => handleFilterChange('category', e.target.value)}
                  >
                    <option value="">All Categories</option>
                    <option value="local">Local</option>
                    {categoryList.map((category) => (
                      <option value={category.id} key={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </CFormSelect>
                )}
                <CDropdown alignment="end" className="ms-auto">
                  <CDropdownToggle color="secondary" variant="outline">
                    <CIcon icon={cilColumns} className="me-1" />
                    Columns
                  </CDropdownToggle>
                  <CDropdownMenu className="p-3">
                    {optionalColumns.map((col) => (
                      <CFormCheck
                        key={col.key}
                        id={`col-${col.key}`}
                        label={col.label}
                        checked={!!visibleColumns[col.key]}
                        onChange={() => toggleColumn(col.key)}
                      />
                    ))}
                  </CDropdownMenu>
                </CDropdown>
              </div>

              <div className="d-flex flex-wrap align-items-center gap-2 mb-3 p-2 bg-light rounded-2">
                <div className="fw-semibold ms-1 me-2">
                  {selectedItems.length} player{selectedItems.length !== 1 ? 's' : ''} selected
                </div>
                <CButton
                  color="secondary"
                  variant="outline"
                  size="sm"
                  disabled={selectedItems.length === 0}
                  onClick={() => setChangeTeamVisible(true)}
                >
                  <CIcon icon={cilSwapHorizontal} className="me-1" />
                  Change Team
                </CButton>
                <CButton
                  color="secondary"
                  variant="outline"
                  size="sm"
                  disabled={selectedItems.length === 0}
                  onClick={() => setSetAvailabilityVisible(true)}
                >
                  <CIcon icon={cilCalendar} className="me-1" />
                  Set Availability
                </CButton>
                {pcrEnabled && (
                  <CButton
                    color="secondary"
                    variant="outline"
                    size="sm"
                    disabled={selectedItems.length === 0}
                    onClick={() => setSetCategoryVisible(true)}
                  >
                    <CIcon icon={cilTag} className="me-1" />
                    Set Position Category Restriction
                  </CButton>
                )}
                {activeTab === 'unavailable' && (
                  <CButton
                    color="danger"
                    variant="outline"
                    size="sm"
                    disabled={selectedItems.length === 0}
                    onClick={() => setBulkDeleteConfirmVisible(true)}
                  >
                    <CIcon icon={cilTrash} className="me-1" />
                    Delete Selected
                  </CButton>
                )}
                <CDropdown alignment="end" className="ms-auto">
                  <CDropdownToggle
                    color="secondary"
                    variant="outline"
                    caret={false}
                    size="sm"
                    disabled={selectedItems.length === 0}
                  >
                    <CIcon icon={cilOptions} />
                  </CDropdownToggle>
                  <CDropdownMenu>
                    <button
                      type="button"
                      className="dropdown-item"
                      onClick={() => setSelectedItems([])}
                    >
                      Clear selection
                    </button>
                  </CDropdownMenu>
                </CDropdown>
              </div>

              <Table
                users={users}
                setUsers={setUsers}
                fetchUsers={fetchUsers}
                setActivePage={setActivePage}
                setItemsPerPage={setItemsPerPage}
                setColumnSorter={setColumnSorter}
                itemsPerPage={itemsPerPage}
                activePage={activePage}
                columnSorter={columnSorter}
                loading={loading}
                visibleColumns={effectiveVisibleColumns}
                selectedItems={selectedItems}
                onSelectedItemsChange={setSelectedItems}
                onOpenPlayer={setDetailPlayerId}
              />
            </>
          )}
        </CCardBody>
      </CCard>

      <AddForm
        visible={addPlayerVisible}
        onClose={() => setAddPlayerVisible(false)}
        fetchUsers={fetchUsers}
      />

      <PlayerDetailPanel
        playerId={detailPlayerId}
        visible={!!detailPlayerId}
        onClose={() => setDetailPlayerId(null)}
        fetchUsers={fetchUsers}
      />

      <ChangeTeamModal
        visible={changeTeamVisible}
        onClose={() => setChangeTeamVisible(false)}
        playerIds={selectedIds}
        teamList={teamList}
        onSuccess={handleBulkActionSuccess}
      />
      <SetAvailabilityModal
        visible={setAvailabilityVisible}
        onClose={() => setSetAvailabilityVisible(false)}
        playerIds={selectedIds}
        onSuccess={handleBulkActionSuccess}
      />
      <SetCategoryModal
        visible={setCategoryVisible}
        onClose={() => setSetCategoryVisible(false)}
        playerIds={selectedIds}
        pcrTerm={pcrTerm}
        onSuccess={handleBulkActionSuccess}
      />
      <Bootbox
        show={bulkDeleteConfirmVisible}
        type={'confirm'}
        message={`Are you sure you want to delete ${selectedIds.length} selected player${
          selectedIds.length !== 1 ? 's' : ''
        }?`}
        onSuccess={handleBulkDeleteConfirm}
        onCancel={() => setBulkDeleteConfirmVisible(false)}
        onClose={() => setBulkDeleteConfirmVisible(false)}
      />
    </>
  )
}

export default Player
